import React, { useState, useEffect, useRef, useCallback } from 'react';
import { api, useAuth } from '../App';
import { Link, useNavigate } from 'react-router-dom';
import { AreaChart, Area, BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, CartesianGrid } from 'recharts';
import OnboardingWizard from '../components/OnboardingWizard';
import LiveActivityFeed from '../components/LiveActivityFeed';
import { useWebSocket } from '../hooks/useWebSocket';
import toast from 'react-hot-toast';

const STATUS_COLORS = { new: '#0284C7', contacted: '#8B5CF6', qualified: '#F59E0B', proposal_sent: '#EC4899', won: '#16A34A', lost: '#DC2626' };
const CHART_COLORS = ['#16A34A', '#0284C7', '#F97316', '#8B5CF6', '#EC4899', '#EAB308'];

const TYPE_COLORS = { call: 'bg-blue-50 text-blue-700', site_visit: 'bg-green-50 text-green-700', demo: 'bg-purple-50 text-purple-700', follow_up: 'bg-orange-50 text-orange-700' };

function UpcomingAppointments() {
  const [appts, setAppts] = useState([]);
  const navigate = useNavigate();
  useEffect(() => {
    api.get('/api/appointments/upcoming?days=7').then(r => setAppts(r.data.appointments || [])).catch(() => {});
  }, []);
  if (appts.length === 0) return null;
  return (
    <div className="card-interactive p-5" data-testid="upcoming-appointments">
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-heading text-base font-semibold text-text-primary flex items-center gap-2"><i className="fa-solid fa-calendar-check text-primary text-sm" />Upcoming Appointments</h3>
        <button onClick={() => navigate('/appointments')} className="text-[10px] font-semibold text-primary hover:underline">View all</button>
      </div>
      <div className="space-y-2">
        {appts.slice(0, 4).map(a => (
          <div key={a.id} className="flex items-center gap-3 p-2.5 rounded-xl bg-surface-alt border border-border/50 hover:border-primary/20 transition-all">
            <div className={`text-[10px] font-semibold px-2 py-1 rounded-lg ${TYPE_COLORS[a.appointment_type] || 'bg-primary/10 text-primary'}`}>
              {new Date(a.scheduled_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-xs font-semibold text-text-primary truncate">{a.title}</p>
              <p className="text-[10px] text-text-muted">{a.lead_name} · {new Date(a.scheduled_at).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}</p>
            </div>
            <span className={`text-[9px] font-bold px-2 py-0.5 rounded-full bg-primary/10 text-primary capitalize`}>{a.appointment_type?.replace(/_/g, ' ')}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function AnimatedCounter({ value, prefix = '', suffix = '', duration = 800 }) {
  const [display, setDisplay] = useState(0);
  const ref = useRef(null);
  const numVal = typeof value === 'string' ? parseFloat(value.replace(/[^0-9.]/g, '')) : value;

  useEffect(() => {
    if (isNaN(numVal)) return;
    let start = 0;
    const startTime = Date.now();
    const step = () => {
      const elapsed = Date.now() - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setDisplay(Math.round(eased * numVal));
      if (progress < 1) ref.current = requestAnimationFrame(step);
    };
    ref.current = requestAnimationFrame(step);
    return () => cancelAnimationFrame(ref.current);
  }, [numVal, duration]);

  if (typeof value === 'string' && value.includes('$')) {
    if (numVal >= 1000000) return <span>{prefix}${(display / 1000000).toFixed(1)}M{suffix}</span>;
    if (numVal >= 1000) return <span>{prefix}${(display / 1000).toFixed(0)}K{suffix}</span>;
    return <span>{prefix}${display}{suffix}</span>;
  }
  return <span>{prefix}{display.toLocaleString()}{suffix}</span>;
}

function Tip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-surface border border-border rounded-xl shadow-card-hover p-3 text-xs">
      <p className="text-text-muted mb-1 font-medium">{label}</p>
      {payload.map((p, i) => <p key={i} style={{ color: p.color }} className="font-semibold">{p.name}: {typeof p.value === 'number' && p.value > 999 ? `$${(p.value / 1000).toFixed(1)}K` : p.value}</p>)}
    </div>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [stats, setStats] = useState(null);
  const [quickStats, setQuickStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [downloadingPdf, setDownloadingPdf] = useState(false);
  const [digest, setDigest] = useState(null);
  const [triggeringDigest, setTriggeringDigest] = useState(false);

  // WebSocket for real-time updates
  const wsUrl = (process.env.REACT_APP_BACKEND_URL || window.location.origin).replace('https://', 'wss://').replace('http://', 'ws://') + '/api/ws/events';
  const { connected, events } = useWebSocket(wsUrl);

  const handleDownloadPdf = async () => {
    setDownloadingPdf(true);
    try {
      const res = await api.get('/api/reports/pdf', { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Samaritan_Solaire_Report_${new Date().toISOString().slice(0, 10)}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch { }
    setDownloadingPdf(false);
  };

  const handleTriggerScan = async () => {
    setTriggeringDigest(true);
    try {
      await api.post('/api/digests/trigger');
      toast.success('Daily scan triggered! Results will appear shortly.');
      // Refresh digest after a delay
      setTimeout(async () => {
        try {
          const res = await api.get('/api/digests/latest');
          setDigest(res.data?.digest);
        } catch {}
        setTriggeringDigest(false);
      }, 15000);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to trigger scan');
      setTriggeringDigest(false);
    }
  };

  useEffect(() => {
    Promise.all([
      api.get('/api/analytics/dashboard'),
      api.get('/api/leads/stats'),
      api.get('/api/dashboard/quick-stats').catch(() => ({ data: null })),
      api.get('/api/digests/latest').catch(() => ({ data: { digest: null } })),
    ]).then(([d, s, q, dg]) => {
      setData(d.data); setStats(s.data); setQuickStats(q.data); setDigest(dg.data?.digest); setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  if (loading) return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 lg:grid-cols-6 gap-4">
        {[...Array(6)].map((_, i) => (
          <div key={i} className="card-interactive p-5 animate-pulse">
            <div className="skeleton h-3 w-16 mb-4" />
            <div className="skeleton h-7 w-20 mb-2" />
            <div className="skeleton h-2 w-24" />
          </div>
        ))}
      </div>
    </div>
  );
  if (!data) return <div className="text-center text-text-muted py-20">Failed to load data</div>;

  const funnel = Object.entries(data.funnel || {}).map(([name, value]) => ({ name: name.replace('_', ' '), value }));
  const totalVal = data.kpis.total_pipeline_value || 0;
  const sources = Object.entries(stats?.source_breakdown || {});
  const qs = quickStats || {};

  return (
    <div className="space-y-6 animate-fade-in" data-testid="dashboard-page">
      {/* Onboarding Wizard */}
      <OnboardingWizard />

      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-3xl font-bold text-text-primary tracking-tight">
            Good {new Date().getHours() < 12 ? 'morning' : new Date().getHours() < 18 ? 'afternoon' : 'evening'}, {user?.name?.split(' ')[0]}
          </h1>
          <p className="text-text-muted text-sm mt-1">Here's your solar lead pipeline at a glance</p>
        </div>
        <div className="flex items-center gap-2">
          <div className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold ${connected ? 'bg-primary/5 text-primary' : 'bg-surface-alt text-text-muted'}`}>
            <span className={`w-2 h-2 rounded-full ${connected ? 'bg-primary animate-pulse-soft' : 'bg-amber-400'}`} />{connected ? 'Live' : 'Synced'}
          </div>
          <button onClick={handleDownloadPdf} disabled={downloadingPdf} className="btn-secondary text-xs py-2" data-testid="download-pdf-report">
            {downloadingPdf ? <i className="fa-solid fa-spinner fa-spin mr-1.5" /> : <i className="fa-solid fa-file-pdf mr-1.5" />}PDF Report
          </button>
          <Link to="/scanner" className="btn-primary text-xs py-2"><i className="fa-solid fa-satellite-dish mr-1.5" />New Scan</Link>
        </div>
      </div>

      {/* KPI Row with Animated Counters */}
      <div className="grid grid-cols-2 lg:grid-cols-4 xl:grid-cols-8 gap-3" data-testid="kpi-grid">
        {[
          { icon: 'fa-users', label: 'Total Leads', value: data.kpis.total_leads, color: 'primary', gradient: 'from-emerald-500 to-green-600' },
          { icon: 'fa-fire', label: 'Qualified', value: data.kpis.qualified_leads, color: 'secondary', gradient: 'from-orange-500 to-amber-600' },
          { icon: 'fa-dollar-sign', label: 'Pipeline', value: totalVal, prefix: '$', isVal: true, color: 'primary', gradient: 'from-emerald-500 to-teal-600' },
          { icon: 'fa-bullseye', label: 'Conversion', value: data.kpis.conversion_rate, suffix: '%', color: 'accent', gradient: 'from-sky-500 to-blue-600' },
          { icon: 'fa-satellite-dish', label: 'Active Scans', value: data.kpis.active_mining_jobs, color: 'secondary', gradient: 'from-orange-500 to-red-500' },
          { icon: 'fa-sparkles', label: 'New Today', value: qs.new_today || 0, color: 'primary', gradient: 'from-green-500 to-emerald-600' },
          { icon: 'fa-brain', label: 'Enriched', value: qs.enriched_leads || 0, color: 'accent', gradient: 'from-blue-500 to-indigo-600' },
          { icon: 'fa-file-invoice', label: 'Proposals', value: qs.total_proposals || 0, color: 'secondary', gradient: 'from-purple-500 to-pink-500' },
        ].map((kpi, i) => (
          <div key={kpi.label} className="card-interactive p-4 group animate-slide-up" style={{ animationDelay: `${i * 50}ms` }} data-testid={`kpi-${kpi.label.toLowerCase().replace(/\s/g, '-')}`}>
            <div className="flex items-center justify-between mb-2">
              <span className="text-[9px] font-bold uppercase tracking-widest text-text-muted">{kpi.label}</span>
              <div className={`w-7 h-7 rounded-lg bg-gradient-to-br ${kpi.gradient} flex items-center justify-center opacity-90 group-hover:opacity-100 group-hover:scale-110 transition-all`}>
                <i className={`fa-solid ${kpi.icon} text-white text-[10px]`} />
              </div>
            </div>
            <p className="font-heading text-xl font-bold text-text-primary tabular-nums count-animate">
              {kpi.isVal ? (
                totalVal >= 1000000 ? <AnimatedCounter value={totalVal / 1000000} prefix="$" suffix="M" /> :
                totalVal >= 1000 ? <AnimatedCounter value={totalVal / 1000} prefix="$" suffix="K" /> :
                <AnimatedCounter value={totalVal} prefix="$" />
              ) : kpi.suffix === '%' ? (
                <><AnimatedCounter value={kpi.value} />{kpi.suffix}</>
              ) : (
                <AnimatedCounter value={kpi.value} prefix={kpi.prefix || ''} />
              )}
            </p>
          </div>
        ))}
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 card-interactive p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-heading text-base font-semibold text-text-primary">Lead Acquisition Trend</h3>
            <span className="text-[10px] font-bold uppercase tracking-widest text-text-muted">Last 30 Days</span>
          </div>
          <div className="h-56">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={data.daily_leads}>
                <defs><linearGradient id="g1" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#16A34A" stopOpacity={0.15} /><stop offset="100%" stopColor="#16A34A" stopOpacity={0} /></linearGradient></defs>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
                <XAxis dataKey="date" tick={{ fill: 'var(--color-text-muted)', fontSize: 10 }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fill: 'var(--color-text-muted)', fontSize: 10 }} axisLine={false} tickLine={false} />
                <Tooltip content={<Tip />} />
                <Area type="monotone" dataKey="leads" stroke="#16A34A" fill="url(#g1)" strokeWidth={2.5} dot={{ r: 3, fill: '#16A34A' }} activeDot={{ r: 5, strokeWidth: 2, stroke: '#fff' }} />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="card-interactive p-6">
          <h3 className="font-heading text-base font-semibold text-text-primary mb-4">Pipeline Funnel</h3>
          <div className="h-48">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie data={funnel} cx="50%" cy="50%" innerRadius={48} outerRadius={72} paddingAngle={3} dataKey="value" nameKey="name">
                  {funnel.map((e) => <Cell key={e.name} fill={STATUS_COLORS[e.name.replace(' ', '_')] || '#A3A7A5'} />)}
                </Pie>
                <Tooltip content={<Tip />} />
              </PieChart>
            </ResponsiveContainer>
          </div>
          <div className="grid grid-cols-2 gap-x-4 gap-y-1.5 mt-3">
            {funnel.map((e) => (
              <div key={e.name} className="flex items-center gap-1.5 text-[11px]">
                <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: STATUS_COLORS[e.name.replace(' ', '_')] || '#A3A7A5' }} />
                <span className="text-text-muted capitalize truncate">{e.name}</span>
                <span className="font-semibold text-text-primary tabular-nums ml-auto">{e.value}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Bottom Row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="card-interactive p-6">
          <h3 className="font-heading text-base font-semibold text-text-primary mb-4">Lead Sources</h3>
          <div className="space-y-3">
            {sources.map(([src, count], i) => {
              const pct = Math.round((count / (data.kpis.total_leads || 1)) * 100);
              return (
                <div key={src} className="animate-slide-up" style={{ animationDelay: `${i * 60}ms` }}>
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-xs text-text-secondary capitalize">{(src || 'unknown').replace('_', ' ')}</span>
                    <span className="text-xs font-semibold text-text-primary tabular-nums">{count} ({pct}%)</span>
                  </div>
                  <div className="h-1.5 bg-surface-alt rounded-full overflow-hidden">
                    <div className="h-full rounded-full transition-all duration-700 ease-out" style={{ width: `${pct}%`, background: CHART_COLORS[i % CHART_COLORS.length] }} />
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <div className="card-interactive p-6">
          <h3 className="font-heading text-base font-semibold text-text-primary mb-4">Score Distribution</h3>
          <div className="h-40">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={data.score_distribution}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
                <XAxis dataKey="range" tick={{ fill: 'var(--color-text-muted)', fontSize: 10 }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fill: 'var(--color-text-muted)', fontSize: 10 }} axisLine={false} tickLine={false} />
                <Tooltip content={<Tip />} />
                <Bar dataKey="count" fill="#0284C7" radius={[6, 6, 0, 0]} name="Leads" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="card-interactive p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-heading text-base font-semibold text-text-primary">Recent Activity</h3>
            <Link to="/leads" className="text-xs text-primary font-semibold hover:underline">View all</Link>
          </div>
          <div className="space-y-3 max-h-[220px] overflow-y-auto">
            {(data.recent_activities || []).map((act, i) => {
              const icons = { call: 'fa-phone text-primary', email: 'fa-envelope text-accent', status_change: 'fa-arrow-right text-secondary', note: 'fa-sticky-note text-warning', mining: 'fa-satellite-dish text-primary', created: 'fa-plus text-success', contact_enrichment: 'fa-address-book text-blue-500', auto_enrichment: 'fa-bolt text-amber-500', bulk_update: 'fa-arrows-rotate text-purple-500' };
              return (
                <div key={act.id || i} className="flex items-start gap-3 animate-slide-up" style={{ animationDelay: `${i * 40}ms` }}>
                  <div className="w-8 h-8 rounded-lg bg-surface-alt flex items-center justify-center flex-shrink-0">
                    <i className={`fa-solid ${icons[act.type] || 'fa-circle text-text-muted'} text-xs`} />
                  </div>
                  <div className="min-w-0">
                    <p className="text-sm text-text-primary leading-snug">{act.description}</p>
                    <p className="text-[11px] text-text-muted mt-0.5">{act.user} &middot; {new Date(act.created_at).toLocaleDateString()}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Quick Actions + Top Markets */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Quick Actions */}
        <div className="card-interactive p-6">
          <h3 className="font-heading text-base font-semibold text-text-primary mb-4">Quick Actions</h3>
          <div className="grid grid-cols-2 gap-3">
            {[
              { label: 'Scan Permits', icon: 'fa-tower-broadcast', path: '/scanner', gradient: 'from-emerald-500 to-green-600' },
              { label: 'Add Lead', icon: 'fa-user-plus', path: '/leads', gradient: 'from-blue-500 to-indigo-600' },
              { label: 'Create Proposal', icon: 'fa-file-invoice', path: '/proposals', gradient: 'from-purple-500 to-pink-500' },
              { label: 'Campaigns', icon: 'fa-bullhorn', path: '/campaigns', gradient: 'from-orange-500 to-red-500' },
              { label: 'Appointments', icon: 'fa-calendar-check', path: '/appointments', gradient: 'from-teal-500 to-cyan-600' },
              { label: 'Install Pipeline', icon: 'fa-helmet-safety', path: '/projects', gradient: 'from-amber-500 to-yellow-500' },
            ].map((action, i) => (
              <Link key={action.label} to={action.path} className="flex items-center gap-3 p-3 rounded-xl bg-surface-alt border border-border/50 hover:border-primary/30 hover:shadow-card transition-all group animate-slide-up" style={{ animationDelay: `${i * 50}ms` }} data-testid={`quick-action-${action.label.toLowerCase().replace(/\s/g, '-')}`}>
                <div className={`w-9 h-9 rounded-lg bg-gradient-to-br ${action.gradient} flex items-center justify-center group-hover:scale-110 transition-transform`}>
                  <i className={`fa-solid ${action.icon} text-white text-xs`} />
                </div>
                <span className="text-sm font-medium text-text-primary">{action.label}</span>
              </Link>
            ))}
          </div>
        </div>

        {/* Top Markets */}
        <div className="card-interactive p-6">
          <h3 className="font-heading text-base font-semibold text-text-primary mb-4">Top Markets</h3>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            {(stats?.top_cities || []).map((c, i) => (
              <div key={c.city} className="p-3 rounded-xl bg-surface-alt border border-border/50 animate-slide-up group hover:border-primary/20 hover:shadow-card transition-all" style={{ animationDelay: `${i * 50}ms` }}>
                <p className="text-sm font-semibold text-text-primary group-hover:text-primary transition-colors">{c.city}</p>
                <p className="text-xs text-text-muted mt-0.5">{c.count} leads</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Daily Scan Digest */}
      <div className="card-interactive p-6" data-testid="scan-digest">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-amber-500 to-orange-600 flex items-center justify-center">
              <i className="fa-solid fa-clock-rotate-left text-white text-sm" />
            </div>
            <div>
              <h3 className="font-heading text-base font-semibold text-text-primary">Auto-Scan Digest</h3>
              <p className="text-xs text-text-muted">Automated daily permit scans across your markets</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <span className="flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-primary/5 text-primary text-[10px] font-semibold">
              <span className="w-1.5 h-1.5 rounded-full bg-primary animate-pulse-soft" />Active
            </span>
            {user?.role === 'admin' && (
              <button onClick={handleTriggerScan} disabled={triggeringDigest} className="btn-secondary text-xs py-1.5 px-3" data-testid="trigger-scan-btn">
                {triggeringDigest ? <><i className="fa-solid fa-spinner fa-spin mr-1.5" />Scanning...</> : <><i className="fa-solid fa-play mr-1.5" />Run Now</>}
              </button>
            )}
          </div>
        </div>
        {digest ? (
          <div className="space-y-4">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <div className="p-3 rounded-xl bg-surface-alt border border-border/50">
                <span className="text-[10px] font-bold uppercase tracking-widest text-text-muted">Leads Found</span>
                <p className="font-heading text-xl font-bold text-text-primary mt-1 tabular-nums">{digest.total_leads_found}</p>
              </div>
              <div className="p-3 rounded-xl bg-surface-alt border border-border/50">
                <span className="text-[10px] font-bold uppercase tracking-widest text-text-muted">High Score</span>
                <p className="font-heading text-xl font-bold text-primary mt-1 tabular-nums">{digest.high_score_leads?.length || 0}</p>
              </div>
              <div className="p-3 rounded-xl bg-surface-alt border border-border/50">
                <span className="text-[10px] font-bold uppercase tracking-widest text-text-muted">Markets</span>
                <p className="font-heading text-xl font-bold text-text-primary mt-1 tabular-nums">{digest.markets_scanned}</p>
              </div>
              <div className="p-3 rounded-xl bg-surface-alt border border-border/50">
                <span className="text-[10px] font-bold uppercase tracking-widest text-text-muted">Last Scan</span>
                <p className="font-heading text-sm font-bold text-text-primary mt-1">{digest.date || 'Today'}</p>
              </div>
            </div>
            {digest.high_score_leads?.length > 0 && (
              <div>
                <h4 className="text-xs font-semibold text-text-secondary mb-2">Top Leads from Latest Scan</h4>
                <div className="space-y-1.5">
                  {digest.high_score_leads.slice(0, 5).map((lead, i) => (
                    <div key={i} className="flex items-center justify-between p-2.5 rounded-xl bg-surface-alt border border-border/50 hover:border-primary/20 transition-all">
                      <div className="flex items-center gap-3 min-w-0">
                        <div className="w-7 h-7 rounded-lg bg-primary/10 flex items-center justify-center text-primary text-xs font-bold">{lead.score}</div>
                        <div className="min-w-0">
                          <p className="text-sm font-semibold text-text-primary truncate">{lead.name}</p>
                          <p className="text-[10px] text-text-muted truncate">{lead.city}, {lead.state} · {lead.solar_potential} potential</p>
                        </div>
                      </div>
                      <div className="text-right flex-shrink-0">
                        <p className="text-sm font-bold text-text-primary tabular-nums">${(lead.estimated_value || 0).toLocaleString()}</p>
                        <p className="text-[10px] text-primary font-semibold">${(lead.estimated_savings || 0).toLocaleString()}/yr savings</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
            {digest.market_results?.length > 0 && (
              <div className="flex flex-wrap gap-2">
                {digest.market_results.map((m, i) => (
                  <span key={i} className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-surface-alt border border-border/50 text-xs font-medium text-text-secondary">
                    <i className="fa-solid fa-map-pin text-primary text-[10px]" />{m.location}
                    <span className="text-text-muted">· {m.leads_found} leads</span>
                  </span>
                ))}
              </div>
            )}
          </div>
        ) : (
          <div className="text-center py-8">
            <i className="fa-solid fa-satellite-dish text-3xl text-text-muted mb-3 block" />
            <p className="text-sm font-semibold text-text-primary">First scan loading...</p>
            <p className="text-xs text-text-muted mt-1">The automated scanner runs every 8 hours. Click "Run Now" to start immediately.</p>
          </div>
        )}
      </div>

      {/* Live Activity Feed */}
      <LiveActivityFeed events={events} connected={connected} />
      {/* Upcoming Appointments strip */}
      <UpcomingAppointments />
    </div>
  );
}
